import { useEffect, useState } from "react";
import api from "../api/axios";
import { SUPERADMIN_DENIED_MESSAGE } from "../constants/roles";

/**
 * The logged-in user (with `roles`/`companies` pivot rows) lives in localStorage["user"], written at
 * login. Roles change on the server (approvals, admin edits), so refreshCurrentUser() re-reads them
 * from GET /api/user and notifies the app through USER_UPDATED_EVENT.
 */
export const USER_UPDATED_EVENT = "auth:user-updated";
const MIN_REFRESH_INTERVAL_MS = 60 * 1000;
// Only this request gets a timeout (the shared axios instance is untouched): a hanging /api/user must
// not keep role-gated routes on "Loading..." forever - it settles and the cached roles are used.
export const REFRESH_TIMEOUT_MS = 10 * 1000;

let lastRefreshAt = 0;
let inflight = null;
let inflightToken = null;
let settled = false; // the first refresh of this page load has finished (success or failure)
const settleListeners = new Set();

/**
 * Tell the app that the stored user changed in THIS tab (right after login). The browser `storage`
 * event only fires in other tabs, so without this the sidebar (CompanyContext) keeps the user it read
 * before login and shows "No company assigned yet" until the page is reloaded.
 */
export function announceUserChange() {
  window.dispatchEvent(new Event(USER_UPDATED_EVENT));
}

export function readStoredUser() {
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function markSettled() {
  settled = true;
  settleListeners.forEach((fn) => fn());
  settleListeners.clear();
}

/**
 * Re-fetch the current user and update localStorage. Throttled to once per minute unless `force`.
 * 401 clears the session (same effect as logging out); other errors keep the stored user.
 */
export async function refreshCurrentUser({ force = false } = {}) {
  const token = localStorage.getItem("token");
  if (!token) {
    markSettled();
    return null;
  }
  // A request started for another token (logout / user switch while it was in flight) is not reused.
  if (inflight && inflightToken === token) return inflight;
  if (!force && Date.now() - lastRefreshAt < MIN_REFRESH_INTERVAL_MS) {
    return readStoredUser();
  }

  const run = (async () => {
    await null; // `run` is assigned before anything below can finish
    try {
      const res = await api.get("/api/user", { timeout: REFRESH_TIMEOUT_MS });
      // The session changed while the request was in flight: this response belongs to someone else.
      if (localStorage.getItem("token") !== token) {
        lastRefreshAt = 0;
        return readStoredUser();
      }
      lastRefreshAt = Date.now();
      const before = localStorage.getItem("user");
      const merged = { ...(readStoredUser() || {}), ...res.data };
      const after = JSON.stringify(merged);
      if (before !== after) {
        localStorage.setItem("user", after);
        window.dispatchEvent(new Event(USER_UPDATED_EVENT));
      }
      return merged;
    } catch (err) {
      if (err?.response?.status === 401 && localStorage.getItem("token") === token) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        window.dispatchEvent(new Event(USER_UPDATED_EVENT));
        return null;
      }
      return readStoredUser();
    } finally {
      if (inflight === run) {
        inflight = null;
        inflightToken = null;
      }
      markSettled();
    }
  })();

  inflight = run;
  inflightToken = token;
  return run;
}

/** Current stored user, kept in sync with refreshes; `ready` is false until the first refresh ends. */
export function useCurrentUser() {
  const [user, setUser] = useState(readStoredUser);
  const [ready, setReady] = useState(settled);

  useEffect(() => {
    const sync = () => setUser(readStoredUser());
    const onSettled = () => setReady(true);

    window.addEventListener(USER_UPDATED_EVENT, sync);
    window.addEventListener("storage", sync);
    if (settled) setReady(true);
    else settleListeners.add(onSettled);
    sync();

    return () => {
      window.removeEventListener(USER_UPDATED_EVENT, sync);
      window.removeEventListener("storage", sync);
      settleListeners.delete(onSettled);
    };
  }, []);

  return { user, ready };
}

/**
 * Error text for a failed call to a Superadmin-only action: a 403 always says so, other
 * errors use the server message or the given fallback.
 */
export function superadminErrorMessage(err, fallback) {
  if (err?.response?.status === 403) return SUPERADMIN_DENIED_MESSAGE;
  return err?.response?.data?.message || err?.response?.data?.error || fallback;
}
